<!-- ========== DESKTOP SIDEBAR (flex child, in document flow) ========== -->
<div
  class="sidebar-wrapper hidden sm:flex"
  [ngClass]="{
    'sidebar-expanded': sidebarPanel.panelOpen && getActiveSection(),
    'sidebar-collapsed': !sidebarPanel.panelOpen || !getActiveSection()
  }"
>
  <!-- ICON RAIL (click section → open sub-panel; label on hover when collapsed) -->
  <nav class="icon-rail" [attr.aria-label]="'MENU.SIDEBAR.MAIN_SECTIONS' | translate">
    <div class="rail-brand">
      <button
        type="button"
        (click)="onPanelToggleClick()"
        class="rail-brand__mark"
        [attr.aria-label]="(sidebarPanel.panelOpen ? 'MENU.SIDEBAR.COLLAPSE_MENU' : 'MENU.SIDEBAR.EXPAND_MENU') | translate"
        [attr.aria-expanded]="sidebarPanel.panelOpen"
        [matTooltip]="(sidebarPanel.panelOpen ? 'MENU.SIDEBAR.COLLAPSE_MENU' : 'MENU.SIDEBAR.EXPAND_MENU') | translate"
        [matTooltipPosition]="railTooltipPosition"
        matTooltipClass="rail-mat-tooltip"
        [matTooltipShowDelay]="150"
      >
        <img src="assets/iconLogo.png" alt="Dashy" />
        <span class="rail-brand__open-icon" aria-hidden="true">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16" />
            <path d="m13 10 2 2-2 2" />
          </svg>
        </span>
      </button>
    </div>

    <div class="rail-body rail-scroll">
      <div class="flex flex-col items-center w-full space-y-0.5">
        <ng-container *ngFor="let section of visibleSections">
          <div *ngIf="section.dividerBefore" class="w-8 border-t border-gray-200/80 my-2"></div>

          <button
            (click)="onRailClick(section.section, $event)"
            class="rail-icon group relative flex items-center justify-center w-full py-1.5 rounded-xl transition-all duration-200"
            [class.rail-icon--active]="sidebarPanel.activeSection === section.section"
            [class.rail-icon--flyout]="flyoutSection === section.section"
            [attr.data-testid]="'rail-section-' + section.section"
            [attr.data-tour]="section.section === 'onboarding' ? 'onboarding-rail' : null"
            [attr.aria-label]="section.label | translate"
            [attr.aria-pressed]="sidebarPanel.activeSection === section.section"
            [attr.aria-expanded]="flyoutSection === section.section"
            [matTooltip]="section.label | translate"
            [matTooltipPosition]="railTooltipPosition"
            matTooltipClass="rail-mat-tooltip"
            [matTooltipShowDelay]="150"
            [matTooltipDisabled]="flyoutSection === section.section"
          >
            <span class="relative">
              <span
                class="rail-icon-box"
                [class.rail-icon-box--active]="sidebarPanel.activeSection === section.section"
              >
                <app-sidebar-icon
                  [name]="section.icon"
                  [size]="18"
                  [strokeWidth]="sidebarPanel.activeSection === section.section ? 2 : 1.5"
                ></app-sidebar-icon>
              </span>
              <span
                *ngIf="section.section === 'messaging' && unreadConversations > 0"
                class="absolute top-0 right-0 min-w-[16px] h-[16px] flex items-center justify-center px-[3px] text-[9px] font-bold text-white bg-red-500 rounded-full leading-none ring-2 ring-[#f3fafa] translate-x-1/4 -translate-y-1/4"
              >{{ unreadConversations > 99 ? '99+' : unreadConversations }}</span>
            </span>
          </button>
        </ng-container>
      </div>

    </div>
    <div class="rail-footer">
      <app-messenger-qr-button [tooltipPosition]="railTooltipPosition"></app-messenger-qr-button>
      <button
        (click)="signout()"
        class="rail-icon rail-icon--logout group relative flex items-center justify-center w-full py-2 mt-2 flex-shrink-0 rounded-xl text-gray-400 hover:text-red-400 transition-all"
        [attr.aria-label]="'APP_USER.LOGOUT' | translate"
        [matTooltip]="'APP_USER.LOGOUT' | translate"
        [matTooltipPosition]="railTooltipPosition"
        matTooltipClass="rail-mat-tooltip"
        [matTooltipShowDelay]="150"
      >
        <app-sidebar-icon name="log-out" [size]="18" [strokeWidth]="1.5"></app-sidebar-icon>
      </button>
    </div>
  </nav>

  <!-- RAIL FLYOUT (floating submenu while the sidebar is collapsed) -->
  <div
    *ngIf="getFlyoutSection() as flyout"
    class="rail-flyout"
    [ngStyle]="flyoutStyle"
    role="menu"
    [attr.aria-label]="flyout.label | translate"
  >
    <div class="rail-flyout__title">{{ flyout.label | translate }}</div>
    <div class="rail-flyout__list">
      <ng-container *ngFor="let menu of flyout.items">
        <ng-container *ngIf="isMenuVisible(menu)">
          <!-- Leaf item -->
          <button
            *ngIf="!menu.items?.length"
            type="button"
            role="menuitem"
            class="rail-flyout__item"
            [class.rail-flyout__item--active]="isMenuItemActive(menu)"
            (click)="onFlyoutNavigate(menu)"
          >
            <span class="rail-flyout__item-label">{{ (menu.label ?? "") | translate }}</span>
            <span
              *ngIf="menu.badge === 'unreadConversations' && unreadConversations > 0"
              class="rail-flyout__badge"
            >{{ unreadConversations > 99 ? "99+" : unreadConversations }}</span>
          </button>

          <!-- Group with sub-items -->
          <ng-container *ngIf="menu.items?.length">
            <button
              *ngIf="menu.path"
              type="button"
              role="menuitem"
              class="rail-flyout__item rail-flyout__item--parent"
              [class.rail-flyout__item--active]="isMenuSectionActive(menu)"
              (click)="onFlyoutNavigate(menu.path)"
            >
              <span class="rail-flyout__item-label">{{ (menu.label ?? "") | translate }}</span>
            </button>
            <div
              *ngIf="!menu.path"
              class="rail-flyout__item rail-flyout__item--parent rail-flyout__item--label"
              role="presentation"
            >
              <span class="rail-flyout__item-label">{{ (menu.label ?? "") | translate }}</span>
            </div>
            <ng-container *ngFor="let sub of menu.items">
              <button
                *ngIf="isSubMenuVisible(sub)"
                type="button"
                role="menuitem"
                class="rail-flyout__item rail-flyout__item--sub"
                [class.rail-flyout__item--active]="isSubItemActive(sub)"
                (click)="onFlyoutNavigate(sub)"
              >
                <span class="rail-flyout__item-label">{{ sub.label | translate }}</span>
              </button>
            </ng-container>
          </ng-container>
        </ng-container>
      </ng-container>
    </div>
  </div>

  <!-- DETAIL PANEL (beside rail, part of flex flow) -->
  <aside
    class="detail-panel flex flex-col h-full bg-white overflow-visible transition-all duration-300 ease-in-out"
    [ngClass]="{
      'detail-panel--open w-[220px] border-r border-gray-200': sidebarPanel.panelOpen && getActiveSection(),
      'detail-panel--pinned': sidebarPanel.panelPinned && sidebarPanel.panelOpen,
      'w-0 min-w-0 border-0': !getActiveSection() || !sidebarPanel.panelOpen
    }"
  >
    <ng-container *ngIf="getActiveSection() as section">
      <ng-container *ngIf="sidebarPanel.panelOpen">
      <!-- Brand header -->
      <div class="detail-panel__brand min-w-[220px]">
        <a routerLink="/dashboard" class="detail-panel__logo-link" aria-label="Dashy">
          <img src="assets/logo.png" alt="Dashy" class="detail-panel__logo" />
        </a>
        <button
          *ngIf="canToggleSubPanel()"
          (click)="onPanelToggleClick()"
          type="button"
          class="detail-panel__brand-toggle"
          [attr.aria-label]="'MENU.SIDEBAR.COLLAPSE_MENU' | translate"
          [attr.aria-expanded]="sidebarPanel.panelOpen"
          [matTooltip]="'MENU.SIDEBAR.COLLAPSE_MENU' | translate"
          [matTooltipPosition]="railTooltipPosition"
          matTooltipClass="rail-mat-tooltip"
          [matTooltipShowDelay]="150"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2"/>
            <path d="M9 4v16"/>
            <path d="m15 10-2 2 2 2"/>
          </svg>
        </button>
      </div>

      <!-- Panel header -->
      <div
        *ngIf="shouldShowPanelHeader(section)"
        class="detail-panel__header flex items-center flex-shrink-0 min-w-[220px]"
      >
        <h2
          class="detail-panel__title flex-1 min-w-0 truncate"
          [class.text-left]="!isRTL"
          [class.text-right]="isRTL"
        >
          {{ getPanelTitle(section) | translate }}
        </h2>
      </div>

      <!-- Panel content -->
      <div class="detail-panel__body flex-1 overflow-y-auto panel-scroll min-w-[220px]">
        <ng-container *ngFor="let menu of section.items">
          <ng-container *ngIf="isMenuVisible(menu)">

            <!-- Single item (no children) -->
            <a
              *ngIf="!menu.items || menu.items.length === 0"
              [routerLink]="menu.path"
              [queryParams]="menu.queryParams || null"
              (click)="onNavigate()"
              class="panel-item"
              [class.panel-item--active]="isMenuItemActive(menu)"
            >
              <span class="flex-1 truncate">{{ menu.label! | translate }}</span>
              <span
                *ngIf="menu.badge === 'unreadConversations' && unreadConversations > 0"
                class="ml-auto min-w-[20px] h-[20px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none"
              >{{ unreadConversations > 99 ? '99+' : unreadConversations }}</span>
            </a>

            <!-- Expandable item (has children) -->
            <div *ngIf="menu.items && menu.items.length > 0" class="panel-group">
              <button
                type="button"
                (click)="onPanelMenuClick(menu)"
                class="panel-item panel-item--parent"
                [class.panel-item--parent-active]="isMenuSectionActive(menu)"
                [class.cursor-default]="menu.alwaysExpanded"
              >
                <span class="flex-1 truncate"
                  [class.text-left]="!isRTL"
                  [class.text-right]="isRTL"
                >{{ menu.label! | translate }}</span>
                <svg
                  *ngIf="!menu.alwaysExpanded"
                  class="w-3 h-3 flex-shrink-0 text-gray-400 transition-transform duration-200"
                  [ngClass]="{ 'rotate-180': isMenuExpanded(menu) }"
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6"
                  aria-hidden="true"
                >
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m1 1 4 4 4-4"/>
                </svg>
              </button>

              <!-- Sub-items -->
              <div
                *ngIf="isMenuExpanded(menu)"
                class="sub-items"
              >
                <ng-container *ngFor="let sub of menu.items">
                  <a
                    *ngIf="isSubMenuVisible(sub)"
                    [routerLink]="sub.path"
                    [queryParams]="sub.queryParams || null"
                    (click)="onNavigate()"
                    class="panel-subitem"
                    [class.panel-item--active]="isSubItemActive(sub)"
                  >
                    <span class="truncate">{{ sub.label | translate }}</span>
                  </a>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </ng-container>
      </div>
      </ng-container>
    </ng-container>
  </aside>
</div>

<!-- ========== MOBILE DRAWER (small screens only) ========== -->
<!-- Opened by the header menu button (SidebarPanelService.mobileOpen); backdrop, Escape and navigation close it. -->
@if (sidebarPanel.mobileOpen()) {
  <div class="mobile-drawer-backdrop sm:hidden" aria-hidden="true" (click)="closeDrawer()"></div>
}
<div
  id="drawer-navigation"
  class="mobile-drawer sm:hidden fixed top-0 z-50 flex flex-col w-[280px] max-w-[85vw] h-screen transition-transform bg-white shadow-2xl"
  [attr.dir]="isRTL ? 'rtl' : 'ltr'"
  [class.left-0]="!isRTL"
  [class.right-0]="isRTL"
  [class.translate-x-0]="sidebarPanel.mobileOpen()"
  [class.-translate-x-full]="!sidebarPanel.mobileOpen() && !isRTL"
  [class.translate-x-full]="!sidebarPanel.mobileOpen() && isRTL"
  [attr.aria-hidden]="!sidebarPanel.mobileOpen()"
  [attr.inert]="sidebarPanel.mobileOpen() ? null : ''"
  [cdkTrapFocus]="sidebarPanel.mobileOpen()"
  [cdkTrapFocusAutoCapture]="sidebarPanel.mobileOpen()"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="'MENU.SIDEBAR.MAIN_SECTIONS' | translate"
  tabindex="-1"
>
  <!-- Mobile header -->
  <div class="mobile-drawer__head flex items-center justify-between px-4 h-[52px] border-b border-gray-100">
    <strong>{{ 'MENU.SIDEBAR.MAIN_SECTIONS' | translate }}</strong>
    <button
      type="button"
      (click)="closeDrawer()"
      aria-controls="drawer-navigation"
      [attr.aria-label]="'METRICS_ADMIN.CLOSE' | translate"
      class="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
    >
      <app-sidebar-icon name="x" [size]="20"></app-sidebar-icon>
    </button>
  </div>

  <!-- Mobile sections -->
  <div class="mobile-drawer__body flex-1 overflow-y-auto px-3 py-3 panel-scroll">
    <ng-container *ngFor="let section of visibleSections">
      <button
        *ngIf="mobileSectionGrouped(section)"
        type="button"
        class="mobile-toggle flex w-full items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium min-w-0"
        (click)="toggleMobileSection(section.section)"
        [attr.aria-expanded]="isMobileSectionExpanded(section.section)"
        [attr.data-mobile-section]="section.section"
        [attr.aria-controls]="isMobileSectionExpanded(section.section) ? 'mobile-section-' + section.section : null"
        [ngClass]="{
          'text-accent font-semibold': isMobileSectionExpanded(section.section),
          'text-gray-600': !isMobileSectionExpanded(section.section)
        }"
      >
        <app-sidebar-icon
          [name]="section.icon"
          [size]="16"
          class="mobile-toggle__icon w-5 flex justify-center opacity-70"
        ></app-sidebar-icon>
        <span class="mobile-toggle__label flex-1 truncate text-left">{{ section.label | translate }}</span>
        <app-sidebar-icon name="chevron-down" [size]="16" class="mobile-toggle__arrow" [class.rotate-180]="isMobileSectionExpanded(section.section)"></app-sidebar-icon>
      </button>

      <ul
        *ngIf="!mobileSectionGrouped(section) || isMobileSectionExpanded(section.section)"
        class="space-y-0.5"
        [id]="'mobile-section-' + section.section"
        [class.mobile-section-items]="mobileSectionGrouped(section)"
      >
        <ng-container *ngFor="let menu of section.items">
          <li *ngIf="isMenuVisible(menu)">
            <a
              *ngIf="!menu.items || menu.items.length === 0"
              (click)="closeDrawer()"
              [routerLink]="menu.path"
              [queryParams]="menu.queryParams || null"
              class="mobile-link flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium"
              [ngClass]="isMenuItemActive(menu) ? 'mobile-link--active' : 'text-gray-600'"
              [attr.aria-current]="isMenuItemActive(menu) ? 'page' : null"
            >
              <app-sidebar-icon
                [name]="menu.icon!"
                [size]="16"
                class="w-5 flex justify-center opacity-70"
              ></app-sidebar-icon>
              <span class="truncate">{{ menu.label! | translate }}</span>
            </a>

            <div *ngIf="menu.items && menu.items.length > 0">
              <button
                *ngIf="!menu.alwaysExpanded; else alwaysOpenMenu"
                type="button"
                (click)="onPanelMenuClick(menu)"
                class="mobile-toggle flex w-full items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium min-w-0"
                [ngClass]="{
                  'text-accent font-semibold': isMenuSectionActive(menu),
                  'text-gray-600': !isMenuSectionActive(menu)
                }"
              >
                <app-sidebar-icon
                  [name]="menu.icon!"
                  [size]="16"
                  class="mobile-toggle__icon w-5 flex justify-center opacity-70"
                ></app-sidebar-icon>
                <span class="mobile-toggle__label flex-1 truncate text-left">{{ menu.label! | translate }}</span>
                <app-sidebar-icon name="chevron-down" [size]="16" class="mobile-toggle__arrow" [class.rotate-180]="isMenuExpanded(menu)"></app-sidebar-icon>
              </button>
              <ng-template #alwaysOpenMenu>
                <div
                  class="flex w-full items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium min-w-0"
                  [ngClass]="{
                    'text-accent font-semibold': isMenuSectionActive(menu),
                    'text-gray-600': !isMenuSectionActive(menu)
                  }"
                >
                  <app-sidebar-icon
                    [name]="menu.icon!"
                    [size]="16"
                    class="w-5 flex justify-center opacity-70"
                  ></app-sidebar-icon>
                  <span class="flex-1 truncate text-left">{{ menu.label! | translate }}</span>
                </div>
              </ng-template>
              <ul *ngIf="isMenuExpanded(menu)" class="mobile-subitems space-y-0.5 mt-0.5">
                <li *ngFor="let sub of menu.items">
                  <a
                    *ngIf="isSubMenuVisible(sub)"
                    (click)="closeDrawer()"
                    [routerLink]="sub.path"
                    [queryParams]="sub.queryParams || null"
                    class="mobile-sub-link mobile-link flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium"
                    [ngClass]="isSubItemActive(sub) ? 'mobile-link--active' : 'text-gray-600'"
                    [attr.aria-current]="isSubItemActive(sub) ? 'page' : null"
                  >
                    <app-sidebar-icon
                      *ngIf="sub.icon"
                      [name]="sub.icon"
                      [size]="16"
                      class="w-5 flex justify-center opacity-70"
                    ></app-sidebar-icon>
                    <span
                      *ngIf="!sub.icon"
                      class="w-5 flex justify-center flex-shrink-0"
                    >
                      <span
                        class="w-[5px] h-[5px] rounded-full"
                        [ngClass]="{
                          'bg-accent': isSubItemActive(sub),
                          'bg-gray-300': !isSubItemActive(sub)
                        }"
                      ></span>
                    </span>
                    <span class="truncate">{{ sub.label | translate }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </li>
        </ng-container>
      </ul>
    </ng-container>
  </div>

  <!-- Mobile footer -->
  <div class="mobile-drawer__footer p-3 border-t border-gray-100 bg-gray-50/50">
    <app-messenger-qr-button [labelled]="true"></app-messenger-qr-button>
    <button type="button" (click)="signout()" class="mobile-signout" data-test="mobile-signout" [attr.aria-label]="'APP_USER.LOGOUT' | translate">
      <app-sidebar-icon name="log-out" [size]="18" [strokeWidth]="1.5"></app-sidebar-icon>
      <span>{{ 'APP_USER.LOGOUT' | translate }}</span>
    </button>
  </div>
</div>
